import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams, useLocation } from "react-router-dom";

import { useMediaQuery } from "react-responsive";
import * as S from "./styles";
import HamburgerButton from "./HamburgerButton";
import MainButton from "./MainButton";
import SubMenu from "./SubMenu";
import { HOMEPAGE_ANCHOR_CONTAINER_IDs } from "../../lib/constants";
import { RESOURCES_TABS } from "./constants";
import { THEME_MEDIA } from "../../theme";
import { isDonationSubdomain } from "../../lib/utils";

const resourcesItem = {
  href: "/resources",
  text: "Resources",
  submenu: {
    key: "resources",
    link: "/blogs",
    tabs: RESOURCES_TABS,
  },
};

const defaultNavItems = [
  {
    href: "/",
    text: "Home",
    key: HOMEPAGE_ANCHOR_CONTAINER_IDs.hero.key,
  },
  {
    href: "/",
    text: "Features",
    key: HOMEPAGE_ANCHOR_CONTAINER_IDs.channels.key,
  },
  resourcesItem,
  {
    href: "/agencies",
    text: "For Agencies",
  },
];

const newHomepageNavItems = [
  {
    text: "Home",
    key: HOMEPAGE_ANCHOR_CONTAINER_IDs.hero.key,
  },
  {
    text: "Features",
    key: HOMEPAGE_ANCHOR_CONTAINER_IDs.channels.key,
  },
  resourcesItem,
  {
    href: "/agencies",
    text: "For Agencies",
  },
];

const contactUsLink = {
  text: "Contact us",
  key: HOMEPAGE_ANCHOR_CONTAINER_IDs.contactUs.key,
};

export function Header({
  noNavigationMenu = false,
  mainButton = { title: "Book a Demo" },
}) {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const isMobile = useMediaQuery({ maxWidth: THEME_MEDIA.mobileForHeader }); // src/theme.js > theme.media.mobileForHeader

  const baseNavItems =
    location.pathname === "/" || location.pathname === "/new_hp"
      ? newHomepageNavItems
      : defaultNavItems;

  // Filter navItems based on showCandidates query parameter
  const navItems = useMemo(() => {
    const showCandidates = searchParams.get("showCandidates") === "true";

    return baseNavItems.map((item) => {
      if (item.submenu && item.text === "Resources") {
        return {
          ...item,
          submenu: {
            ...item.submenu,
            tabs: item.submenu.tabs.filter(
              (tab) => tab.title !== "" || showCandidates
            ),
          },
        };
      }
      return item;
    });
  }, [searchParams, baseNavItems]);

  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpenedMenu, setIsOpenedMenu] = useState(false);
  const [isOpenedSubmenu, setIsOpenedSubmenu] = useState(
    baseNavItems
      .filter((c) => c.submenu)
      .reduce((a, c) => ({ ...a, [c.text]: false }), {})
  );
  const [featuredBlogPost, setFeaturedBlogPost] = useState(null);
  const [guide, setGuide] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener("scroll", handleScroll);

    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleClickNavLink = () => {
    setIsOpenedMenu(false);
  };

  const formatLinkHref = (link) => {
    if (link.key) {
      const baseHref = !link.href
        ? ""
        : link.href.endsWith("/")
          ? link.href
          : `${link.href}/`;
      return `${baseHref}#sc-${link.key}`;
    }

    return link.href || "#";
  };

  const toggleSubmenu = (item, isOpen) => {
    setIsOpenedSubmenu({
      ...isOpenedSubmenu,
      [item.text]: isOpen ?? !isOpenedSubmenu[item.text],
    });
  };

  const renderSubmenu = (item, forMobile) => {
    const className = `NavMenuItem-${Math.random()}`;
    return (
      <S.NavMenuItem
        className={className}
        key={item.text}
        $isOpen={isOpenedSubmenu[item.text] ?? false}
        onClick={() => toggleSubmenu(item)}
      >
        <span className={className}>{item.text}</span>
        {!!isOpenedSubmenu[item.text] && isMobile === forMobile && (
          <SubMenu
            data={item.submenu}
            onClickItem={() => toggleSubmenu(item, false)}
            excludeClassname={className}
            {...{
              isMobile,
              featuredBlogPost,
              setFeaturedBlogPost,
              guide,
              setGuide,
            }}
          />
        )}
      </S.NavMenuItem>
    );
  };

  return (
    <>
      <S.StyledHeader $isScrolled={isScrolled}>
        <S.HeaderContainer>
          <S.LogoLink to="/" $isDisabled={isDonationSubdomain()}>
            <S.Logo
              src="/images/logos/reach-logo.svg"
              alt="ReachThePeople.ai Logo"
            />
          </S.LogoLink>
          {!noNavigationMenu && (
            <S.Navigation>
              {navItems.map((item) =>
                item.submenu ? (
                  renderSubmenu(item, false)
                ) : (
                  <S.NavLink
                    key={item.text}
                    href={formatLinkHref(item)}
                    onClick={handleClickNavLink}
                  >
                    {item.text}
                  </S.NavLink>
                )
              )}
            </S.Navigation>
          )}
          <S.ActionsContainer>
            {!noNavigationMenu && (
              <S.ContactLink
                href={formatLinkHref(contactUsLink)}
                onClick={handleClickNavLink}
              >
                {contactUsLink.text}
              </S.ContactLink>
            )}
            <MainButton
              data={mainButton}
              formatLinkHref={formatLinkHref}
              handleClickNavLink={handleClickNavLink}
              variant="header"
            />
            {!noNavigationMenu && (
              <HamburgerButton
                initialOpen={isOpenedMenu}
                onClick={setIsOpenedMenu}
              />
            )}
          </S.ActionsContainer>
        </S.HeaderContainer>
      </S.StyledHeader>

      <S.MobileNavigationContainer $isOpened={isOpenedMenu}>
        <S.MobileNavigation>
          <S.MobileNavItems>
            {[...navItems].map((item) =>
              item.submenu ? (
                renderSubmenu(item, true)
              ) : (
                <S.NavLink
                  key={item.text}
                  href={formatLinkHref(item)}
                  onClick={handleClickNavLink}
                >
                  {item.text}
                </S.NavLink>
              )
            )}
          </S.MobileNavItems>
          <MainButton
            data={mainButton}
            formatLinkHref={formatLinkHref}
            handleClickNavLink={handleClickNavLink}
            variant="subMenu"
          />
        </S.MobileNavigation>
      </S.MobileNavigationContainer>
    </>
  );
}
